// IFCViewerAdapter — konkrétní ViewerAdapter nad IFC Viewer SDK (§8, §36).
// Toto je JEDINÉ místo BCF vrstvy, které zná SDK. Náhrada enginu = nový adapter,
// beze změny BCF core/services/workflow/repository/UI.
import { ViewerAdapter } from "./ViewerAdapter.js";
import { componentRef } from "../core/model.js";

// Převody směrových vektorů mezi scénou (Y nahoru) a IFC (Z nahoru).
function sceneDirToIfc(d) { return { x: d.x, y: -d.z, z: d.y }; }
function ifcDirToScene(d) { return { x: d.x, y: d.z, z: -d.y }; }
function norm(v) { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; }

export class IFCViewerAdapter extends ViewerAdapter {
  constructor(viewer) {
    super();
    if (!viewer) throw new Error("IFCViewerAdapter: 'viewer' (IFCViewer) je povinný.");
    this.viewer = viewer;
  }

  primaryModelId() { const m = this.viewer.listModels(); return m.length ? m[0].modelId : null; }
  listModels() { return this.viewer.listModels().map((m) => ({ modelId: m.modelId, name: m.name })); }
  hasComponent(modelId, globalId) { return this.viewer.hasObject(modelId, globalId); }

  // --- Kamera (IFC souřadnice) ----------------------------------------------
  getCamera() {
    const cs = this.viewer.getCameraState();      // scéna: { position, target }
    const posIfc = this.viewer.sceneToIfc(cs.position);
    const targetIfc = this.viewer.sceneToIfc(cs.target);
    const dir = norm({ x: targetIfc.x - posIfc.x, y: targetIfc.y - posIfc.y, z: targetIfc.z - posIfc.z });
    return {
      type: "perspective",
      position: posIfc,
      direction: dir,
      up: sceneDirToIfc({ x: 0, y: 1, z: 0 }),    // scéna Y nahoru → IFC Z nahoru
      fieldOfView: 60,
      _targetDist: Math.hypot(cs.target.x - cs.position.x, cs.target.y - cs.position.y, cs.target.z - cs.position.z) || 10,
    };
  }
  setCamera(cam) {
    if (!cam || !cam.position || !cam.direction) return;
    const posScene = this.viewer.ifcToScene(cam.position);
    const dirScene = ifcDirToScene(cam.direction);
    const D = cam._targetDist || 10;
    const target = { x: posScene.x + dirScene.x * D, y: posScene.y + dirScene.y * D, z: posScene.z + dirScene.z * D };
    this.viewer.setCameraState({ position: posScene, target }, true);
  }

  // --- Výběr -----------------------------------------------------------------
  getSelectedComponents() {
    return this.viewer.getSelection().map((s) => componentRef({ modelId: s.modelId, globalId: s.globalId }));
  }
  selectComponents(refs) { return this.viewer.select(this._toRefs(refs)); }
  clearSelection() { this.viewer.clearSelection(); }
  highlightComponents(refs) { return this.viewer.select(this._toRefs(refs), { highlight: true }); }

  // --- Viditelnost -----------------------------------------------------------
  getVisibility() {
    const st = this.viewer.getState();
    if (st.isolatedObjects && st.isolatedObjects.length) {
      return { defaultVisibility: false, exceptions: st.isolatedObjects.map((r) => componentRef(r)) };
    }
    return { defaultVisibility: true, exceptions: (st.hiddenObjects || []).map((r) => componentRef(r)) };
  }
  applyVisibility(v) {
    if (!v) return;
    this.viewer.showAll();
    const refs = this._toRefs(v.exceptions || []);
    if (v.defaultVisibility === false) { if (refs.length) this.viewer.isolate(refs); }
    else if (refs.length) this.viewer.hide(refs);
  }
  showAll() { this.viewer.showAll(); }

  // --- Řezné roviny ----------------------------------------------------------
  getClippingPlanes() { return this.viewer.getClippingPlanes(); }
  setClippingPlanes(planes) { this.viewer.setClippingPlanes(planes || []); }
  clearClippingPlanes() { if (this.viewer.sections) this.viewer.sections.clear(); }

  // --- Barvy -----------------------------------------------------------------
  async applyColoring(coloring) {
    for (const c of coloring || []) {
      if (c && c.color && c.components && c.components.length) await this.viewer.setColor(this._toRefs(c.components), c.color);
    }
  }
  resetColors() { this.viewer.resetColors(); }

  // --- Snímek + reset --------------------------------------------------------
  captureSnapshot() { return this.viewer.captureSnapshot(); }
  resetView() { this.viewer.showAll(); this.viewer.resetColors(); if (this.viewer.sections) this.viewer.sections.clear(); this.viewer.clearSelection(); this.viewer.fitToModel(); }

  // Doplní chybějící modelId primárním modelem (federace: jinak nechá dané).
  _toRefs(refs) {
    const pm = this.primaryModelId();
    return (refs || []).map((r) => ({ modelId: r.modelId || pm, globalId: r.globalId })).filter((r) => r.globalId);
  }
}
